<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: $t('ui.entities.reloop.reloopScorecard', 2), to: '/reports/reloop/scorecard' }
    ]"
  >
    <template #right>
      <div>
        <DatePicker
          v-model="dateRange"
          mode="range"
          :presets="dateRangePresets"
          @select="onDateRangeChange"
        />
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="dealershipScores"
      removable-sort
      sort-mode="multiple"
      export-filename="ReLoop Scorecard"
      :sort-order="1"
      :rows="20"
      data-key="id"
      :paginator="true"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-reloop-scorecard"
      state-storage="session"
      show-gridlines
      :loading="apiRequest.isLoading.value"
      :global-filter-fields="['id', 'dealershipId', 'dealershipName']"
    >
      <template #empty>
        <Empty
          v-if="!apiRequest.isLoading.value"
          title="No data"
        />
      </template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>
      <!-- <Column selection-mode="multiple" header-style="width: 3em"></Column> -->
      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
        :show-filter-match-modes="false"
        :show-filter-operator="false"
      >
        <template #body="{ data }">
          <router-link
            v-tippy="`View submissions`"
            class="link"
            :to="{ path: '/reports/reloop/submissions', query: { dealerships: data.dealershipId } }"
          >
            {{ data.dealershipName }}
          </router-link>
        </template>
        <template #filter="{ filterModel }">
          <Select
            :teleport="false"
            v-model="filterModel.value"
            :options="dealershipsList"
            mode="multiple"
            searchable
            :chips="false"
            value-key="value"
            label-key="label"
            placeholder="Any"
            class="min-w-64"
          >
            <template #option="slotProps">
              {{ slotProps.option.label }}
            </template>
          </Select>
        </template>
      </Column>
      <Column
        field="submissions"
        header="Submitted"
        :sortable="true"
      />
      <Column
        field="settlementPercent"
        header="Settlement"
        :sortable="true"
        class="no-padding"
      >
        <template #body="{ data }">
          <div
            :style="{
              'background-color': 'var(--bg-' + data.settlementColor + ')',
              color: 'var(--' + data.settlementColor + ')'
            }"
          >
            {{ numberFormatter(data.settlementPercent) }}%
          </div>
        </template>
      </Column>
      <Column
        field="valuedPercent"
        header="Valued"
        :sortable="true"
        class="no-padding"
      >
        <template #body="{ data }">
          <div
            :style="{
              'background-color': 'var(--bg-' + data.valuedColor + ')',
              color: 'var(--' + data.valuedColor + ')'
            }"
          >
            {{ numberFormatter(data.valuedPercent) }}%
          </div>
        </template>
      </Column>
      <Column
        field="testDrivePercent"
        header="Test Drive"
        :sortable="true"
        class="no-padding"
      >
        <template #body="{ data }">
          <div
            :style="{
              'background-color': 'var(--bg-' + data.testDriveColor + ')',
              color: 'var(--' + data.testDriveColor + ')'
            }"
          >
            {{ numberFormatter(data.testDrivePercent) }}%
          </div>
        </template>
      </Column>
      <Column
        field="metManagementPercent"
        header="Met Management"
        :sortable="true"
        class="no-padding"
      >
        <template #body="{ data }">
          <div
            :style="{
              'background-color': 'var(--bg-' + data.metManagementColor + ')',
              color: 'var(--' + data.metManagementColor + ')'
            }"
          >
            {{ numberFormatter(data.metManagementPercent) }}%
          </div>
        </template>
      </Column>
      <Column
        field="finalOptionsPercent"
        header="Final Options"
        :sortable="true"
        class="no-padding"
      >
        <template #body="{ data }">
          <div
            :style="{
              'background-color': 'var(--bg-' + data.finalOptionsColor + ')',
              color: 'var(--' + data.finalOptionsColor + ')'
            }"
          >
            {{ numberFormatter(data.finalOptionsPercent) }}%
          </div>
        </template>
      </Column>
      <Column
        field="totals.totalPercent"
        header="Score"
        :sortable="true"
      >
        <template #body="{ data }">
          <span v-tippy="String(data.totals.totalScore)">
            {{ data.totals.totalPercent != null ? toRoundedPercent(data.totals.totalPercent / 100) : '-' }}
          </span>
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, computed } from 'vue'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Empty from '@/components/empty/Empty.vue'
import Select from '@/components/forms/Select.vue'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'

import dayjs from 'dayjs'

// stores
import { useAuthStore } from '@/stores/AuthStore.ts'

// composables
import useApiRequest from '@/composables/useApiRequest'

// utilities
import { alertToast } from '@/utilities/notification'
import { toRoundedPercent } from '@/utilities'
import { datePresets } from '@/utilities/date-time'

const dt = ref()
const authStore = useAuthStore()
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)

const apiRequest = useApiRequest()
const reloopsFetched = ref([])

const dateRange = ref({
  start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  end: dayjs().format('YYYY-MM-DD')
})
const dateRangePresets = [datePresets.thisWeek, datePresets.thisMonth, datePresets.lastMonth]

function fetchReloops() {
  try {
    apiRequest
      .send({
        endpoint: '/v1/reloop',
        params: {
          startDate: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
          endDate: dayjs(dateRange.value.end).format('YYYY-MM-DD'),
          dealerships: dealershipsAllowed.value.map(d => d.id).join(',')
        }
      })
      .then(response => {
        if (response.success) {
          reloopsFetched.value = response.data
        } else {
          alertToast('Failed to fetch ReLoops', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch ReLoops', err.message, 'error')
  }
}
fetchReloops()

const filters = ref([])

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dealershipId: { value: null, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

const dealershipsList = computed(() => {
  return dealershipsAllowed.value.map(d => {
    return {
      value: d.id,
      label: d.name
    }
  })
})

function getScorePercent(score, max) {
  // Check that both numbers are not null
  if (score === null || max === null) {
    return 0
  }
  return (score / max) * 100
}

function getScoreColor(score) {
  if (score <= 70) {
    return 'danger'
  } else if (score <= 80) {
    return 'warning'
  } else {
    return 'success'
  }
}

const dealershipScores = computed(() => {
  const grouped = reloopsFetched.value.reduce((acc, submission) => {
    const { dealershipId, dealershipName, scores } = submission
    if (!acc[dealershipId]) {
      acc[dealershipId] = {
        dealershipId,
        dealershipName,
        submissions: 0,
        settlement: 0,
        valued: 0,
        testDrive: 0,
        metManagement: 0,
        finalOptions: 0,
        totals: {
          totalScore: 0,
          totalPossible: 0,
          totalPercent: 0
        }
      }
    }

    acc[dealershipId].submissions++
    acc[dealershipId].settlement += scores.answers.settlementGiven
    acc[dealershipId].valued += scores.answers.vehicleValued
    acc[dealershipId].testDrive += scores.answers.testDriveOffered
    acc[dealershipId].metManagement += scores.answers.metManagement
    acc[dealershipId].finalOptions += scores.answers.finalOptionsGiven

    acc[dealershipId].totals.totalScore += scores.totals.totalScore
    acc[dealershipId].totals.totalPossible += scores.totals.totalPossible

    // getScorePercent for each
    acc[dealershipId].settlementPercent = getScorePercent(acc[dealershipId].settlement, acc[dealershipId].submissions)
    acc[dealershipId].settlementColor = getScoreColor(acc[dealershipId].settlementPercent)
    acc[dealershipId].valuedPercent = getScorePercent(acc[dealershipId].valued, acc[dealershipId].submissions)
    acc[dealershipId].valuedColor = getScoreColor(acc[dealershipId].valuedPercent)
    acc[dealershipId].testDrivePercent = getScorePercent(acc[dealershipId].testDrive, acc[dealershipId].submissions)
    acc[dealershipId].testDriveColor = getScoreColor(acc[dealershipId].testDrivePercent)
    acc[dealershipId].metManagementPercent = getScorePercent(
      acc[dealershipId].metManagement,
      acc[dealershipId].submissions
    )
    acc[dealershipId].metManagementColor = getScoreColor(acc[dealershipId].metManagementPercent)
    acc[dealershipId].finalOptionsPercent = getScorePercent(
      acc[dealershipId].finalOptions,
      acc[dealershipId].submissions
    )
    acc[dealershipId].finalOptionsColor = getScoreColor(acc[dealershipId].finalOptionsPercent)

    return acc
  }, {})

  // Calculate total percent
  Object.values(grouped).forEach(dealership => {
    dealership.totals.totalPercent = Math.round((dealership.totals.totalScore / dealership.totals.totalPossible) * 100)
  })

  return Object.values(grouped)
})

function onDateRangeChange() {
  // console.log('onDateRangeChange', dateRange.value)
  // Refetch reloops
  fetchReloops()
}

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
